import { Box, Stack, Typography } from '@mui/material';
import {
  calculateUpgradePath,
  EscalationRule,
  findNextExpectationChange,
  formatResponseExpectation,
  formatRuleResponseLabel,
  formatTimeShort,
  HolidayWindow,
  matchEscalationRule,
  SERVICE_LEVEL_PRIORITY_ORDER,
  TimeWindow,
} from '@provision/sidekick-utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMemo } from 'react';
import { formatEnumLabel } from './escalation-utils';

dayjs.extend(relativeTime);

interface EscalationCardProps {
  rules: EscalationRule[];
  urgencyLevel?: string | null;
  serviceLevel: string;
  currentTime: Date;
  timezone?: string;
  /**
   * Partner's actual business hours. Used for deadline calculations (e.g., "by end of day").
   */
  businessHours?: TimeWindow[];
  /**
   * Hours during which partners accept escalation notifications.
   * Used for condition matching. Falls back to businessHours when not set.
   */
  escalationCutoffHours?: TimeWindow[];
  holidays?: HolidayWindow[];
  expanded: boolean;
  isPreLits?: boolean;
}

/** how far ahead the collapsed card warns that the expectation is about to slip */
const EXPECTATION_CHANGE_WARNING_MINUTES = 30;

const OVERLINE_SX = {
  display: 'block',
  mb: 0.5,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.6875rem',
} as const;

export const EscalationCard = ({
  rules,
  urgencyLevel,
  serviceLevel,
  currentTime,
  timezone,
  businessHours,
  escalationCutoffHours,
  holidays,
  expanded,
  isPreLits,
}: EscalationCardProps) => {
  const memoizedContext = useMemo(
    () => ({
      currentTime,
      timezone,
      businessHours,
      escalationCutoffHours,
      holidays,
      isPreLits,
    }),
    [currentTime, timezone, businessHours, escalationCutoffHours, holidays, isPreLits],
  );

  const {
    match,
    expectation,
    nextChange,
    upgradePath,
    currentExpectationText,
    upgradeText,
    isHighestTier,
  } = useMemo(() => {
    const matchedByRule = matchEscalationRule(rules, serviceLevel, memoizedContext);
    const upcomingChange = findNextExpectationChange(rules, serviceLevel, memoizedContext);
    const path = calculateUpgradePath(rules, serviceLevel, memoizedContext).slice(0, 5);

    const currentOrder = SERVICE_LEVEL_PRIORITY_ORDER.indexOf(serviceLevel);
    const highest = currentOrder === SERVICE_LEVEL_PRIORITY_ORDER.length - 1;

    const expectationText = buildCurrentExpectationText(
      matchedByRule?.rule ?? null,
      serviceLevel,
    );
    const upText = buildUpgradeText(path, highest, urgencyLevel);

    return {
      match: matchedByRule,
      expectation: formatResponseExpectation(matchedByRule, serviceLevel, timezone),
      nextChange: upcomingChange,
      upgradePath: path,
      currentExpectationText: expectationText,
      upgradeText: upText,
      isHighestTier: highest,
    };
  }, [rules, serviceLevel, memoizedContext, urgencyLevel, timezone]);

  // only a slip matters: an expectation quoted now and escalated after the change would be
  // promising more than the partner commits to
  const imminentChange =
    nextChange &&
    dayjs(nextChange.at).diff(currentTime, 'minute', true) <=
      EXPECTATION_CHANGE_WARNING_MINUTES &&
    isLaterDeadline(match?.deadline ?? null, nextChange.match?.deadline ?? null)
      ? nextChange
      : null;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">{expectation}</Typography>
        {imminentChange ? (
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
            (changes {dayjs(imminentChange.at).from(dayjs(currentTime))})
          </Typography>
        ) : null}
        {!imminentChange && match?.deadline ? (
          <Typography variant="caption" color="text.secondary">
            ({dayjs(match.deadline).from(dayjs(currentTime), true)})
          </Typography>
        ) : null}
      </Stack>

      {urgencyLevel === 'urgent' ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.8125rem' }}
        >
          Mention after-hours rates to the client before escalating
        </Typography>
      ) : null}

      {expanded ? (
        <Box sx={{ mt: 2 }}>
          {/* CURRENT EXPECTATION section */}
          <Typography variant="caption" color="text.primary" sx={OVERLINE_SX}>
            Current Expectation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, fontSize: '0.8125rem' }}
          >
            {currentExpectationText}
          </Typography>

          {nextChange ? (
            <>
              <Typography variant="caption" color="text.primary" sx={OVERLINE_SX}>
                Next Expectation
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2.5, fontSize: '0.8125rem' }}
              >
                From {formatTimeShort(nextChange.at, timezone)},{' '}
                {lowerFirst(
                  formatResponseExpectation(nextChange.match, serviceLevel, timezone),
                )}
              </Typography>
            </>
          ) : null}

          {/* IF CLIENT UPGRADES section */}
          <Typography variant="caption" color="text.primary" sx={OVERLINE_SX}>
            If Client Upgrades
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', fontSize: '0.8125rem' }}
          >
            {upgradeText}
          </Typography>

          {/* Upgrade path details */}
          {!isHighestTier && upgradePath.length > 0 ? (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {upgradePath
                .filter((u) => !u.unavailable)
                .map((upgrade) => (
                  <Typography
                    key={upgrade.serviceLevel}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    {upgrade.serviceLevel} → response by{' '}
                    {formatTimeShort(upgrade.deadline, timezone)}
                  </Typography>
                ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

/** a lost commitment counts as later */
function isLaterDeadline(current: Date | null, next: Date | null): boolean {
  if (!current) {
    return false;
  }
  return !next || next > current;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function buildCurrentExpectationText(
  rule: EscalationRule | null,
  serviceLevel: string,
): string {
  if (!rule) {
    return `Not available for ${serviceLevel} clients.`;
  }

  const parts: string[] = [];

  if (rule.serviceLevels === 'all') {
    parts.push('All service levels');
  } else if (rule.serviceLevels.length > 0) {
    parts.push(`${rule.serviceLevels.join(' or ')} clients`);
  } else {
    parts.push(`${serviceLevel} clients`);
  }

  const timeLabel = formatEnumLabel(rule.timeCondition.type);
  if (rule.timeCondition.type !== 'always') {
    parts.push(timeLabel.toLowerCase());
  }

  parts.push(`response ${formatRuleResponseLabel(rule)}`);

  return `${parts.join(', ')}.`;
}

function buildUpgradeText(
  upgradePath: { serviceLevel: string; unavailable: boolean; deadline: Date | null }[],
  isHighestTier: boolean,
  urgencyLevel?: string | null,
): string {
  if (isHighestTier) {
    return 'This client is already at the highest tier.';
  }

  const available = upgradePath.filter((u) => !u.unavailable);
  if (available.length === 0) {
    const urgency = (urgencyLevel ?? 'normal').toLowerCase();
    if (urgency === 'normal') {
      return 'Normal escalations have the same response time for all membership tiers.';
    }
    if (urgency === 'life-safety') {
      return 'Life safety response is the same for all membership tiers.';
    }
    return 'No improved response time available through tier upgrade.';
  }

  return `Upgrading the client's membership tier may improve response times:`;
}
